import type {
  Contact,
  ContactInput,
  ContactPatch,
  ContactPurpose,
  ContactResolution,
} from '../domain/contact';
import { contactResolutionSchema, contactSchema } from '../domain/validation';
import type { ContactsApi } from './contacts-api';
import { ApiError, ConflictError, ForbiddenError, NotFoundError, ValidationError } from './errors';
import { withoutUndefined } from './patch';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

function redirectToSignIn(): void {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/google?returnTo=${encodeURIComponent(returnTo)}`);
}

// gRPC status codes leatherback returns through Envoy.
const INVALID_ARGUMENT = 3;
const NOT_FOUND = 5;
const PERMISSION_DENIED = 7;
const FAILED_PRECONDITION = 9;
const ABORTED = 10;

export class LiveContactsApi implements ContactsApi {
  private async fetchJSON(path: string, init?: RequestInit): Promise<Record<string, unknown>> {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        ...init,
        headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
      });
    } catch (error) {
      throw new ApiError(
        error instanceof Error ? error.message : 'Network request failed',
        'NETWORK',
      );
    }
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) {
        redirectToSignIn();
        throw new ApiError('Your session expired. Redirecting to sign-in…', 'UNAUTHENTICATED');
      }
      const message =
        typeof body.message === 'string'
          ? body.message
          : typeof body.error === 'string'
            ? body.error
            : `Request failed with status ${response.status}`;
      switch (body.code) {
        case NOT_FOUND:
          throw new NotFoundError(message);
        case ABORTED:
          throw new ConflictError(message);
        case PERMISSION_DENIED:
          throw new ForbiddenError(message);
        case INVALID_ARGUMENT:
          throw new ValidationError(message);
        case FAILED_PRECONDITION:
          // "PRIMARY_TAKEN: Kari is already the primary receiving contact for site BHM"
          throw new ValidationError(message.replace(/^[A-Z_]+:\s*/, ''));
      }
      if (response.status === 404) throw new NotFoundError(message);
      throw new ApiError(message, 'INTERNAL');
    }
    return body;
  }

  private async listAll(params: URLSearchParams): Promise<Contact[]> {
    const contacts: Contact[] = [];
    let pageToken = '';
    do {
      const query = new URLSearchParams(params);
      query.set('pageSize', '500');
      if (pageToken) query.set('pageToken', pageToken);
      const raw = await this.fetchJSON(`/v1/contacts?${query.toString()}`);
      const rows = Array.isArray(raw.contacts) ? raw.contacts : [];
      contacts.push(...rows.map((row) => contactSchema.parse(row)));
      pageToken = typeof raw.nextPageToken === 'string' ? raw.nextPageToken : '';
    } while (pageToken);
    return contacts;
  }

  async listCustomerContacts(customerId: string, includeInactive = false): Promise<Contact[]> {
    const params = new URLSearchParams({ customerId });
    if (includeInactive) params.set('includeInactive', 'true');
    return this.listAll(params);
  }

  async listSiteContacts(siteId: string): Promise<Contact[]> {
    return this.listAll(new URLSearchParams({ siteId }));
  }

  async createContact(input: ContactInput): Promise<Contact> {
    const raw = await this.fetchJSON('/v1/contacts', {
      method: 'POST',
      body: JSON.stringify(withoutUndefined(input)),
    });
    return contactSchema.parse(raw);
  }

  async updateContact(patch: ContactPatch): Promise<Contact> {
    const { id, clear, ...rest } = patch;
    const params = new URLSearchParams();
    // clearMask uses the proto field names.
    if (clear?.length) params.set('clearMask', clear.join(','));
    const query = params.toString();
    const raw = await this.fetchJSON(
      `/v1/contacts/${encodeURIComponent(id)}${query ? `?${query}` : ''}`,
      {
        method: 'PATCH',
        body: JSON.stringify(withoutUndefined({ ...rest, version: String(rest.version) })),
      },
    );
    return contactSchema.parse(raw);
  }

  async deleteContact(id: string): Promise<void> {
    await this.fetchJSON(`/v1/contacts/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async resolveContacts(siteId: string, purpose?: ContactPurpose): Promise<ContactResolution[]> {
    const query = purpose ? `?purpose=${purpose}` : '';
    const raw = await this.fetchJSON(
      `/v1/sites/${encodeURIComponent(siteId)}/contacts:resolve${query}`,
    );
    const rows = Array.isArray(raw.resolutions) ? raw.resolutions : [];
    return rows.map((row) => contactResolutionSchema.parse(row));
  }
}

export const contactsApi = new LiveContactsApi();
